<template>
    <form id="mainForm" action="#" method="post">
        <Card>
            <template #title>
                <Toolbar>
                    <template #left>
                        <div class="p-card-title">
                            [{{ mode }}] {{ $t('employee') }}
                        </div>
                        <div style="margin-left: 10px" v-if="mode != this.$FORM_MODE_CREATE">
                            <Button type="button" icon="pi pi-info" @click="toggle" aria:haspopup="true"
                                aria-controls="overlay_panel" class="p-button-rounded p-button-outlined p-button-sm" />

                            <OverlayPanel ref="op" appendTo="body" :showCloseIcon="true" id="overlay_panel"
                                style="width: 550px" :breakpoints="{ '960px': '80vw' }">
                                <div class="grid field">
                                    <div class="row">
                                        <i class="pi pi-plus-circle" /> {{ this.$formatDateTime(header.Created_Date) }}
                                    </div>
                                    <div class="row">
                                        &nbsp;by {{ header.Created_By }}
                                    </div>
                                </div>
                                <div class="grid field" v-if="header.Edited_Date">
                                    <div class="row">
                                        <i class="pi pi-pencil" /> {{ this.$formatDateTime(header.Edited_Date) }}
                                    </div>
                                    <div class="row">
                                        &nbsp;by {{ header.Edited_By }}
                                    </div>
                                </div>
                                <div class="grid field" v-if="header.Deleted_Date">
                                    <div class="row">
                                        <i class="pi pi-trash" /> {{ this.$formatDateTime(header.Deleted_Date) }}
                                    </div>
                                    <div class="row">
                                        &nbsp;by {{ header.Deleted_By }}
                                    </div>
                                </div>
                            </OverlayPanel>
                        </div>
                    </template>

                    <template #right>
                        <Button icon="pi pi-chevron-left" label="Back" class="p-button mr-2" @click="backToIndex" />
                        <Button icon="pi pi-save" label="Save" class="p-button-success" @click="confirm($event)"
                            v-if="canEdit && !this.disableField" />
                        <Button icon="pi pi-trash" label="Delete" class="p-button-danger" @click="confirm($event)"
                            v-if="mode == this.$FORM_MODE_DELETE" />
                        <ConfirmPopup></ConfirmPopup>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="p-inputtext-sm p-fluid">
                    <div class="grid">
                        <div class="col-6">
                            <div class="field grid" v-if="header">
                                <label class="col-4">
                                    <b>{{ $t('emp_data') }}</b>
                                </label>
                                <div class="col-8 md-8">
                                    <b>{{ this.header.Employee_Period }}</b>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Source" class="col-4">{{ $t('source') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Source" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/lookup'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{ group: 'SOURCE' }"
                                        :class="{ 'p-invalid': submitted && !header.Source }"
                                        @change="this.ResetStructure()" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Source && submitted && canEdit" class="p-error">{{
                                        $t('is_required').replace('Value', $t('source')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="NIK" class="col-4">{{ $t('nik') }}<span style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <InputText name="NIK" v-model="header.NIK" type="text" :placeholder="$t('nik')"
                                        v-if="header && (header.Source == 'CHECK ROLL' || this.$store.getters.getNonGroup == 'Y')"
                                        :class="{ 'p-invalid': submitted && !header.NIK }"
                                        :disabled="!canEdit || this.disableField" />
                                    <InputNumber name="NIK" v-model="header.NIK" type="text" :placeholder="$t('nik')"
                                        v-else-if="header"
                                        :class="{ 'p-invalid': submitted && (!header.NIK || (header.NIK && this.header.NIK.toString().length < 8)) }"
                                        :disabled="!canEdit || this.disableField" :useGrouping="false"
                                        :min-fraction-digits="0" :max="9999999999" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.NIK && submitted && canEdit" class="p-error">{{
                                        $t('is_required').replace('Value', $t('nik')) }}
                                    </small>
                                    <small
                                        v-if="header && header.NIK && submitted && canEdit && this.header.NIK.toString().length < 8 && header.Source != 'CHECK ROLL'"
                                        class="p-error">{{ $t('nik_length') }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Name" class="col-4">{{ $t('name') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <InputText name="Name" v-model="header.Name" required="true" type="text"
                                        :placeholder="$t('name')" v-if="header"
                                        :class="{ 'p-invalid': submitted && !header.Name }"
                                        :disabled="!canEdit || this.disableField" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Name && submitted && canEdit" class="p-error">{{
                                        $t('is_required').replace('Value', $t('name')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Birth_Date" class="col-4">{{ $t('birthdate') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <DatePicker tabindex="3" name="Birth_Date" v-model="header.Birth_Date"
                                        :showOnFocus="true" :placeholder="$t('birthdate')" v-if="header"
                                        :class="{ 'p-invalid': submitted && !header.Birth_Date }"
                                        :disabled="!canEdit || this.disableField" dateFormat="dd/mm/yyyy"
                                        inputMask="99/99/9999" momentFormat="DD/MM/YYYY" style="width : 100%" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Birth_Date && submitted && canEdit"
                                        class="p-error">{{
                                        $t('is_required').replace('Value', $t('birthdate')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Work_Begin_Date" class="col-4">{{ $t('workdate') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <DatePicker tabindex="3" name="Work_Begin_Date" v-model="header.Work_Begin_Date"
                                        :showOnFocus="true" :placeholder="$t('workdate')" v-if="header"
                                        :class="{ 'p-invalid': submitted && !header.Work_Begin_Date }"
                                        :disabled="!canEdit || this.disableField" dateFormat="dd/mm/yyyy"
                                        inputMask="99/99/9999" momentFormat="DD/MM/YYYY" style="width : 100%" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Work_Begin_Date && submitted && canEdit"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('workdate')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Email" class="col-4">{{ $t('email') }}</label>
                                <div class="col-8 md-8">
                                    <InputText name="Email" v-model="header.Email" required="true" type="text"
                                        :placeholder="$t('email')" v-if="header"
                                        :disabled="!canEdit || this.disableField" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small v-if="header && !header.Email && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('email')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Grade_Code" class="col-4">{{ $t('grade') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Grade_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/grade_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}"
                                        :class="{ 'p-invalid': submitted && !header.Grade_Code }" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Grade_Code && submitted && canEdit"
                                        class="p-error">{{
                                        $t('is_required').replace('Value', $t('grade')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Grade_Begin" class="col-4">{{ $t('grade_begin') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <DatePicker tabindex="3" name="Grade_Begin" v-model="header.Grade_Begin"
                                        :showOnFocus="true" :placeholder="$t('grade_begin')" v-if="header"
                                        :class="{ 'p-invalid': submitted && !header.Grade_Begin }"
                                        :disabled="!canEdit || this.disableField" dateFormat="dd/mm/yyyy"
                                        inputMask="99/99/9999" momentFormat="DD/MM/YYYY" style="width : 100%" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Grade_Begin && submitted && canEdit"
                                        class="p-error">{{
                                        $t('is_required').replace('Value', $t('grade_begin')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Job_Status_Code" class="col-4">{{ $t('jobStatus') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Job_Status_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/job_status_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}"
                                        :class="{ 'p-invalid': submitted && !header.Job_Status_Code }" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Job_Status_Code && submitted && canEdit"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('jobStatus')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Education" class="col-4">{{ $t('education') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Education" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/lookup'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{ group: 'EDUCATION' }" />
                                    <Skeleton v-else-if="!header" />
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Method_Functional_Code" class="col-4">{{ $t('method_func') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Method_Functional_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/method_functional_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}" />
                                    <Skeleton v-else-if="!header" />
                                </div>
                            </div>                            
                            <div class="field grid">
                                <DataTable v-if="header" :loading="!header"
                                    :value="header.AppraisalYear ? header.AppraisalYear : null"
                                    responsiveLayout="scroll" class="p-datatable-sm editable-cells-table col-12">
                                    <Column :exportable="false" style="width:30px">
                                        <template #header>
                                            <Button label="" icon="pi pi-plus"
                                                class="p-button-primary p-mr-2 p-button-sm" @click="addDetail()"
                                                :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT" />
                                        </template>
                                        <template #body="slotProps">
                                            <Button icon="pi pi-minus" class="p-button-danger p-mr-2 p-button-sm"
                                                @click="deleteDetail(slotProps)"
                                                :disabled="!canEdit || (slotProps.data.Year != this.$store.getters.getPeriorYear && slotProps.data.Year != null)"
                                                :class="{ 'row-hidden': slotProps.data.mode == this.$FORM_MODE_DELETE }" />
                                        </template>
                                    </Column>
                                    <Column field="Year" :header="$t('appraisal_year')" key="Year">
                                        <template #body="slotProps">
                                            <div class="p-fluid">
                                                <Dropdown v-model="slotProps.data[slotProps.column.props.field]"
                                                    :options="years" optionLabel="value" optionValue="value"
                                                    :placeholder="$t('year')"
                                                    :disabled="!canEdit || (slotProps.data[slotProps.column.props.field] != this.$store.getters.getPeriorYear && slotProps.data[slotProps.column.props.field] != null)"
                                                    :class="{ 'row-hidden': slotProps.data.mode == this.$FORM_MODE_DELETE }"
                                                    @change="onCellEditComplete(slotProps)" />
                                            </div>
                                        </template>
                                    </Column>
                                    <Column field="Active" :header="$t('active')" key="Active" style="width:50px;">
                                        <template #body="slotProps">
                                            <div class="p-fluid">
                                                <ToggleButton v-model="slotProps.data[slotProps.column.props.field]"
                                                    onLabel="Yes" offLabel="No" :style="{ width: '10em' }"
                                                    :class="{ 'row-hidden': slotProps.data.mode == this.$FORM_MODE_DELETE }"
                                                    :disabled="!canEdit || (slotProps.data.Year != this.$store.getters.getPeriorYear && slotProps.data.Year != null)"
                                                    @change="onCellEditComplete(slotProps)" />
                                            </div>
                                        </template>
                                    </Column>
                                    <Column>
                                        <template #body="slotProps">
                                            <Button icon="pi pi-list" v-tooltip.bottom="'Historical Employee'"
                                                class="p-button-rounded p-button-secondary p-button-outlined mr-2 p-button-sm"
                                                @click="viewLog(slotProps.data.Year)"
                                                :class="{ 'row-hidden': slotProps.data.mode == this.$FORM_MODE_DELETE }"
                                                v-if="slotProps.data.NIK != null" />
                                        </template>
                                    </Column>
                                </DataTable>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="field grid">
                                <label for="Company_Code" class="col-4">&nbsp;</label>
                                <div class="col-8 md-8"></div>
                            </div>
                            <div class="field grid">
                                <label for="Company_Code" class="col-4">{{ $t('company') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Company_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/company_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}"
                                        :class="{ 'p-invalid': submitted && !header.Company_Code }" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Company_Code && submitted && canEdit"
                                        class="p-error">{{
                                        $t('is_required').replace('Value', $t('company')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Unit_Code" class="col-4">{{ $t('unit') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Unit_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/unit_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}"
                                        :class="{ 'p-invalid': submitted && !header.Unit_Code }" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Unit_Code && submitted && canEdit" class="p-error">{{
                                        $t('is_required').replace('Value', $t('unit')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Region" class="col-4">{{ $t('business_unit') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Region" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/lookup'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{ group: 'REGION' }"
                                        :class="{ 'p-invalid': submitted && !header.Region }" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Region && submitted && canEdit" class="p-error">{{
                                        $t('is_required').replace('Value', $t('business_unit')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Position_Code" class="col-4">{{ $t('position') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Position_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/position_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small
                                        v-if="header && !header.Position_Code && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('position')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid" v-if="header && header.Source != 'HCM'">
                                <label for="Structure" class="col-4">{{ $t('structure') }}</label>
                                <div class="col-8 md-8">
                                    <InputText name="Structure" v-model="header.Structure" required="true" type="text"
                                        :placeholder="$t('structure')" v-if="header"
                                        :disabled="!canEdit || this.disableField" />
                                    <Skeleton v-else-if="!header" />
                                </div>
                            </div>
                            <div class="field grid" v-else>
                                <label for="Structure" class="col-4">{{ $t('org_unit') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Structure" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/org_unit_desc'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small
                                        v-if="header && !header.Functional_Code && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('functional')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Division_Code" class="col-4">{{ $t('division') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Division_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/division_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small
                                        v-if="header && !header.Division_Code && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('division')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Department_Code" class="col-4">{{ $t('department') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Department_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/department_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small
                                        v-if="header && !header.Department_Code && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('department')) }}
                                    </small> -->
                                </div>
                            </div>
                            <!-- <div class="field grid">
                                <label for="Section_Code" class="col-4">{{ $t('section') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Section_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/section_code'" :minLength="0"
                                        :disabled="!canEdit" :params="{}" />
                                    <Skeleton v-else-if="!header" />
                                </div>
                            </div> -->
                            <div class="field grid">
                                <label for="Directorate_Code" class="col-4">{{ $t('directorate') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Directorate_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/directorate_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small
                                        v-if="header && !header.Directorate_Code && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('directorate')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Functional_Code" class="col-4">{{ $t('functional') }}</label>
                                <div class="col-8 md-8">
                                    <CustomSelect class="p-inputtext-sm" v-model="header.Functional_Code" v-if="header"
                                        :filter="true" :url="this.$API_URL + 'list/functional_code'" :minLength="0"
                                        :disabled="!canEdit || this.disableField" :params="{}" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small
                                        v-if="header && !header.Functional_Code && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('functional')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Receive_Notification" class="col-4">{{ $t('receive_notification') }}</label>
                                <div class="col-8 md-8">
                                    <ToggleButton v-model="header.Receive_Notification" onLabel="Yes" offLabel="No"
                                        :style="{ width: '10em' }" v-if="header"
                                        :disabled="!canEdit || this.disableField" />
                                    <Skeleton v-else-if="!header" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>
    </form>
</template>


<script>
export default {
    name: "Editor",
    data() {
        return {
            indexName: "EmployeeIndex",
            url: this.$API_URL + "employee/",
            header: null,
            detail: null,
            mode: "",
            submitted: false,
            id: 0,
            formEditable: true,
            fromMenu: "",
            disableField: false
        }
    },
    methods: {
        getData() {
            var self = this;
            this.$axios.get(this.url + this.id + "/" + this.mode)
                .then((response) => {
                    if (response.data.success) {
                        self.header = response.data.data.header;
                        self.header.mode = self.mode;
                        self.appraisalyear = response.data.data.appraisalyear;
                    }
                })
                .catch(function (error) {
                    alert(error);
                });
        },
        saveData() {
            this.$emit('block-ui');
            var self = this;
            this.$axios.post(this.url, this.header)
                .then((response) => {
                    if (response.data.success) {
                        if (this.mode == this.$FORM_MODE_DELETE) {
                            self.$router.push({ name: self.indexName, params: { showToast: true, severity: 'success', summary: 'Data Deleted', detail: "Employee " + self.header.NIK + " has been deleted successfully", life: 3000 } });
                        } else {
                            if (self.fromMenu == "Appraisal") {
                                self.$router.push({ path: "/Employee/Index/", query: { title: 'Appraisal' }, params: { showToast: true, severity: 'success', summary: 'Data Saved', detail: "Employee " + self.header.NIK + " has been saved successfully", life: 3000 } });
                            }
                            else {
                                self.$router.push({ name: "EmployeeIndex", params: { showToast: true, severity: 'success', summary: 'Data Saved', detail: "Employee " + self.header.NIK + " has been saved successfully", life: 3000 } });
                            }
                        }
                    }
                    else {
                        self.showError('Error Saving Data', response.data.message);
                    }
                })
                .catch(function (error) {
                    self.showError('Error Saving Data', error);
                });
        },
        showError(summary, message) {
            this.$emit('unblock-ui');
            this.$toast.add({ severity: 'error', summary: summary, detail: message, life: 3000 });
        },
        backToIndex() {
            this.$router.push({ path: "/Employee/Index" });
        },
        toggle(event) {
            this.$refs.op.toggle(event);
        },
        confirm(event) {
            this.submitted = true;
            if (!this.header.NIK || !this.header.Name || !this.header.Birth_Date || !this.header.Work_Begin_Date || !this.header.Grade_Begin
                || !this.header.Grade_Code || !this.header.Company_Code || !this.header.Unit_Code || !this.header.Region || !this.header.Source ||
                (this.header.NIK && this.header.NIK.toString().length < 8 && this.header.Source != 'CHECK ROLL')) {
                return;
            } else {
                this.$confirm.require({
                    target: event.currentTarget,
                    message: 'Are you sure?',
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        this.saveData();
                    },
                    reject: () => {
                    }
                });
            }
        },
        hideRow(data) {
            return data.mode == this.$FORM_MODE_DELETE ? 'row-hidden' : null;
        },
        addDetail() {
            var newDetail = this.appraisalyear;
            newDetail.mode = this.$FORM_MODE_CREATE;
            var det = { ...newDetail };

            if (this.header.Job_Status_Code == "11") {
                det.Active = false;
            }
            else {
                det.Active = true;
            }

            this.header.AppraisalYear.push(det);
        },
        deleteDetail(event) {
            if (event) {
                var detail = this.header.AppraisalYear[event.index];

                if (detail.mode == this.$FORM_MODE_CREATE) this.header.AppraisalYear.splice(this.header.AppraisalYear.indexOf(detail), 1);
                else detail.mode = this.$FORM_MODE_DELETE;
            }
        },
        onCellEditComplete(event) {
            this.editDetail(event.index);
        },
        editDetail(index) {
            if (this.header && this.header.AppraisalYear) {
                let mode = this.header.AppraisalYear[index].mode;

                if (mode == this.$FORM_MODE_UNCHANGED) {
                    this.header.AppraisalYear[index].mode = this.$FORM_MODE_EDIT;
                }
            }
        },
        ResetStructure() {
            this.header.Structure = null;
        },
        viewLog(year) {
            var self = this;
            var listDetail = self.header.AppraisalYear;
            self.header = self.appraisalyear.filter(a => a.Year == year)[0];
            self.header.AppraisalYear = listDetail;
            self.header.Employee_Period = year;
            self.header.mode = self.mode;

            if (listDetail.length > 1 && year != self.$store.getters.getPeriorYear) {
                self.disableField = true;
            }
            else {
                self.disableField = false;
            }
        }
    },
    created() {
        this.mode = this.$route.query.mode;
        this.id = this.$route.query.id ?? 0;
        this.fromMenu = this.$route.query.fromMenu;

        this.getData();
    },
    computed: {
        years() {
            let years = [];
            for (var i = new Date().getFullYear(); i >= 2023; i--) {
                years.push({ value: i.toString() })
            }
            return years;
        },
        canEdit() {
            return this.mode == this.$FORM_MODE_CREATE || this.mode == this.$FORM_MODE_EDIT;
        },
    }
}
</script>